// Sign-in and set-password screens. Both render inside AuthShell, the shared
// centered brand card, so the styling lives in exactly one place.
import { useState } from "react";
import { pb } from "./pb";
import { isValidEmail } from "./calc";
import { Logo } from "./icons";

const inputStyle = {width:"100%",background:"var(--input-bg)",border:"1px solid var(--border2)",color:"var(--text)",padding:"10px 12px",borderRadius:8,fontFamily:"inherit",fontSize:14,outline:"none",boxSizing:"border-box"};
const pwInputStyle = {...inputStyle,padding:"10px 40px 10px 12px"};
const primaryBtnStyle = {background:"var(--btn-primary-bg)",color:"var(--btn-primary-c)",border:"none",padding:"11px",borderRadius:8,fontFamily:"inherit",fontSize:14,fontWeight:600,cursor:"pointer",marginTop:4};

function FieldLabel({ children }) {
  return <div style={{fontSize:11,fontWeight:600,color:"var(--text2)",letterSpacing:".06em",textTransform:"uppercase",marginBottom:6}}>{children}</div>;
}
function Alert({ kind, children }) {
  const s = kind === "error"
    ? {background:"#fdecea",border:"1px solid #e89090",color:"#8a1a1a"}
    : {background:"#eaf6ec",border:"1px solid #9ccfa6",color:"#1a5a2a"};
  return <div style={{...s,borderRadius:8,padding:"8px 12px",fontSize:13,marginBottom:16}}>{children}</div>;
}

function AuthShell({ subtitle, children }) {
  return (
    <div style={{minHeight:"100vh",background:"var(--bg)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Hanken Grotesk',sans-serif",padding:16}}>
      <div style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:14,padding:"36px 32px",width:"100%",maxWidth:380,boxShadow:"0 8px 32px rgba(0,0,0,.12)"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:9,marginBottom:4,color:"var(--accent)"}}>
          <Logo size={28} />
          <span style={{fontSize:26,fontWeight:800,letterSpacing:"-.04em",color:"var(--ink)"}}>OTTracker</span>
        </div>
        <p style={{fontSize:12,color:"var(--text3)",textAlign:"center",letterSpacing:".08em",textTransform:"uppercase",marginBottom:28}}>{subtitle}</p>
        {children}
      </div>
    </div>
  );
}

function EyeToggle({ show, onToggle }) {
  return (
    <button type="button" onClick={onToggle}
      style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",padding:4,color:"var(--ink-3)",lineHeight:1}}>
      {show
        ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19M1 1l22 22"/></svg>
        : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
      }
    </button>
  );
}

export function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleLogin(e) {
    e.preventDefault();
    setLoading(true); setError(""); setNotice("");
    try {
      await pb.collection("users").authWithPassword(email, password);
      // Belt-and-suspenders next to the pb_hooks auth block: if this PB
      // instance doesn't run the hook, a deactivated account still
      // authenticates — catch it here and refuse the session.
      if (pb.authStore.model && pb.authStore.model.active === false) {
        pb.authStore.clear();
        setError("This account has been deactivated. Contact your admin.");
      } else {
        onLogin(pb.authStore.model);
      }
    } catch(err) {
      // PocketBase ClientResponseError carries an HTTP status; a status of 0
      // means the request never reached the server (offline / wrong URL / CORS).
      if (err && err.status === 0) {
        setError("Can't reach the server — check your connection and try again.");
      } else if (err && err.status === 403) {
        setError("This account has been deactivated. Contact your admin.");
      } else {
        setError("Invalid email or password");
      }
    }
    setLoading(false);
  }

  // Send a password-reset email. Always shows the same neutral message so the
  // form never reveals whether an account exists (no account enumeration).
  async function handleForgot() {
    setError(""); setNotice("");
    if (!isValidEmail(email)) { setError("Enter your email above, then tap Forgot password."); return; }
    setLoading(true);
    try { await pb.collection("users").requestPasswordReset(email.trim()); } catch(_) {}
    setLoading(false);
    setNotice("If an account exists for that email, a password-reset link is on its way.");
  }

  return (
    <AuthShell subtitle="Overtime timesheet">
      {error && <Alert kind="error">{error}</Alert>}
      {notice && <Alert kind="ok">{notice}</Alert>}
      <form onSubmit={handleLogin} style={{display:"flex",flexDirection:"column",gap:14}}>
        <div>
          <FieldLabel>Email</FieldLabel>
          <input type="email" value={email} onChange={e=>setEmail(e.target.value)} required style={inputStyle} />
        </div>
        <div>
          <FieldLabel>Password</FieldLabel>
          <div style={{position:"relative"}}>
            <input type={showPw?"text":"password"} value={password} onChange={e=>setPassword(e.target.value)} required style={pwInputStyle} />
            <EyeToggle show={showPw} onToggle={()=>setShowPw(v=>!v)} />
          </div>
        </div>
        <button type="submit" disabled={loading} style={{...primaryBtnStyle,opacity:loading?0.6:1}}>
          {loading ? "Signing in..." : "Sign In"}
        </button>
      </form>
      <button type="button" onClick={handleForgot} disabled={loading}
        style={{display:"block",margin:"16px auto 0",background:"none",border:"none",color:"var(--text2)",fontFamily:"inherit",fontSize:12.5,cursor:"pointer",textDecoration:"underline",opacity:loading?0.6:1}}>
        Forgot password?
      </button>
    </AuthShell>
  );
}

// Set-password screen — reached from the emailed reset/first-time link
// (?pwreset=<token>). Used for both first-time setup and ordinary resets.
export function ResetScreen({ token, onDone }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (pw.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (pw !== pw2) { setError("Passwords don't match."); return; }
    setLoading(true);
    try {
      await pb.collection("users").confirmPasswordReset(token, pw, pw2);
      setDone(true);
    } catch(err) {
      setError(err && err.status === 0
        ? "Can't reach the server — try again."
        : "This link is invalid or has expired. Request a new one from the sign-in screen.");
    }
    setLoading(false);
  }

  return (
    <AuthShell subtitle="Set your password">
      {done ? (
        <>
          <Alert kind="ok">Password set. You can sign in now.</Alert>
          <button type="button" onClick={onDone} style={{...primaryBtnStyle,width:"100%",marginTop:0}}>
            Go to sign in
          </button>
        </>
      ) : (
        <>
          {error && <Alert kind="error">{error}</Alert>}
          <form onSubmit={submit} style={{display:"flex",flexDirection:"column",gap:14}}>
            <div>
              <FieldLabel>New password</FieldLabel>
              <div style={{position:"relative"}}>
                <input type={showPw?"text":"password"} value={pw} onChange={e=>setPw(e.target.value)} required autoFocus style={pwInputStyle} />
                <button type="button" onClick={()=>setShowPw(v=>!v)} style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",padding:4,color:"var(--ink-3)",lineHeight:1,fontSize:12}}>{showPw?"Hide":"Show"}</button>
              </div>
              <div style={{fontSize:11,color:"var(--text3)",marginTop:5}}>At least 8 characters.</div>
            </div>
            <div>
              <FieldLabel>Confirm password</FieldLabel>
              <input type={showPw?"text":"password"} value={pw2} onChange={e=>setPw2(e.target.value)} required style={inputStyle} />
            </div>
            <button type="submit" disabled={loading} style={{...primaryBtnStyle,opacity:loading?0.6:1}}>
              {loading ? "Saving..." : "Set password"}
            </button>
          </form>
        </>
      )}
    </AuthShell>
  );
}
